import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "@webscopeio/react-textarea-autocomplete/style.css";
import cn from "classnames";
import moment from "moment";
import { DefaultButton, PrimaryButton } from "office-ui-fabric-react";
import React, { useState } from "react";
import {
  DeleteCommentMutation,
  ToggleResolveCommentMutation,
  UpdateCommentMutation
} from "gql/mutations/comment.graphql";
import { Modal } from "../../../../common/components";
import { gqlMutation, withTooltip } from "../../../../common/lib";
import { Comment } from "../../../wrappers/Comment";
import { CommentInput } from "./CommentInput";

interface IProps {
  comment: Comment
  canDeleteAny: boolean
  editting: boolean
  onEdit: () => void
  onClose: () => void
  autoFocus?: boolean
}

export const CommentEntry = ({ comment, canDeleteAny, editting, onEdit, onClose, autoFocus }: IProps) => {
  const [confirmingToDelete, setConfirmingToDelete] = useState(false);

  const handleResolve = (mutateFunc) => {
    const gid = comment.gid;
    mutateFunc({ variables: { gid } });
  };

  const handleUpdate = (mutateFunc, value: string) => {
    const input = { gid: comment.gid, comment: value };
    onClose();
    mutateFunc({ variables: { input } });
  };

  const handleDelete = (mutateFunc) => {
    setConfirmingToDelete(false);
    mutateFunc({ variables: { gid: comment.gid } });
  };

  const convertTime = (time) => moment(time).format("MMM DD, YYYY h:mma");

  const currentUserId = window.current_user?.id;
  const canDelete = canDeleteAny || (`${comment.user?.id}` === currentUserId && !comment.system_generated);
  const canEdit = !comment.resolved && `${comment.user?.id}` === currentUserId && !comment.system_generated;

  return (
    <div className="comment flex mt-1em bg-white p-12 rounded-5px">
      <div className={cn("flex-1 flex flex-col", { "italic text-darkergray": comment.resolved })}>
        <div className="flex items-center mb-8">
          <span className="mr-24 text-14 font-bold" data-testid="comment-author">
            {comment.user!.full_name}
            {comment.system_generated && (
              <span className="font-bold italic"> (System Generated)</span>
            )}
          </span>
          <span data-testid="comment-timestamp">{convertTime(comment.created_at)}</span>
          {canEdit && (
            <span
              className="ml-16 text-blue text-12 font-bold cursor-pointer"
              onClick={editting ? onClose : onEdit}
              data-testid="button-edit-comment"
            >
              {editting ? "Cancel" : "Edit"}
            </span>
          )}
        </div>
        {!editting && (
          <div className="whitespace-pre-wrap" data-testid="comment-content">
            {comment.content}
            {comment.edited_at &&
              withTooltip(
                convertTime(comment.edited_at),
                <span className="text-darkergray ml-8" data-testid="comment-edited-marker">
                  {" (Edited)"}
                </span>
              )}
          </div>
        )}
        {editting &&
          gqlMutation(UpdateCommentMutation, (mutateFunc) => (
            <CommentInput
              className="comment-edit border rounded-2px p-2"
              buttonSize={16}
              buttonClassName="p-4 mb-4"
              value={comment.content || ""}
              onSave={(value) => handleUpdate(mutateFunc, value)}
              onCancel={onClose}
              autoFocus={autoFocus}
            />
          ))}
      </div>

      <div className="flex flex-col items-end min-w-100px max-w-100px font-bold">
        {gqlMutation(ToggleResolveCommentMutation, (mutateFunc) => (
          <div className="comment-action h-16px mb-8" data-testid="button-toggle-resolve">
            {!comment.resolved && (
              <a className="text-14 text-blue cursor-pointer" onClick={() => handleResolve(mutateFunc)}>
                Resolve
              </a>
            )}
            {comment.resolved && (
              <>
                <a className="action text-14 text-blue cursor-pointer" onClick={() => handleResolve(mutateFunc)}>
                  Recover
                </a>
                <span className="desc text-14 text-darkergray italic">Resolved</span>
              </>
            )}
          </div>
        ))}
        {canDelete &&
          gqlMutation(DeleteCommentMutation, (mutateFunc) => (
            <>
              <FontAwesomeIcon
                className="text-darkgray cursor-pointer"
                icon={["fal", "trash-can"]}
                onClick={() => setConfirmingToDelete(true)}
                data-testid="button-delete-comment"
              />
              {confirmingToDelete && (
                <Modal
                  title="Delete Comment"
                  onClose={() => setConfirmingToDelete(false)}
                  data-testid="delete-comment-modal"
                >
                  <div className="text-20 pt-16 pb-24">This action will delete this comment</div>
                  <div className="flex justify-between">
                    <DefaultButton
                      className="bg-red hover:bg-red text-white hover:text-white mr-16"
                      onClick={() => handleDelete(mutateFunc)}
                      data-testid="button-yes-on-delete-modal"
                    >
                      Yes
                    </DefaultButton>
                    <PrimaryButton onClick={() => setConfirmingToDelete(false)} data-testid="button-no-on-delete-modal">
                      No
                    </PrimaryButton>
                  </div>
                </Modal>
              )}
            </>
          ))}
      </div>
    </div>
  );
};
